import { IconButton, Tooltip, useToast } from '@chakra-ui/react';
import {
  ShiftReportRecord,
  ShiftReportsResponse,
  ShiftStatus,
} from '@bofrak-backend/shared';
import { useState } from 'react';
import { FiDownload, FiFileText } from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';
import { apiAdapter } from '../../api/backend';
import { useSharedUiTranslation } from '../../../i18n';

interface Props {
  merchantId: string;
  storeId: string;
  shiftId: string;
  shiftStatus: ShiftStatus;
}

const TERMINAL: ReadonlySet<ShiftStatus> = new Set([
  ShiftStatus.CLOSED,
  ShiftStatus.CLOSED_WITH_VARIANCE,
]);

const ShiftReportButton = ({
  merchantId,
  storeId,
  shiftId,
  shiftStatus,
}: Props) => {
  const { t } = useSharedUiTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const isTerminal = TERMINAL.has(shiftStatus);

  const { data, isLoading, refetch } = useQuery<ShiftReportsResponse, Error>(
    ['shift-report', merchantId, storeId, shiftId],
    () =>
      apiAdapter.listShiftReports({
        merchant_id: merchantId,
        store_id: storeId,
        shift_id: shiftId,
        limit: 1,
      }),
    {
      enabled: isTerminal && Boolean(merchantId && storeId && shiftId),
      refetchOnWindowFocus: false,
    },
  );

  if (!isTerminal) return null;

  const existing: ShiftReportRecord | undefined = data?.reports?.[0];

  if (existing?.download_url) {
    return (
      <IconButton
        as="a"
        aria-label={t('Open PDF')}
        href={existing.download_url}
        target="_blank"
        rel="noopener noreferrer"
        icon={<FiDownload />}
        size="xs"
        colorScheme="blue"
        variant="ghost"
      />
    );
  }

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const { record } = await apiAdapter.generateShiftReport({
        merchant_id: merchantId,
        store_id: storeId,
        shift_id: shiftId,
      });
      await qc.invalidateQueries([
        'shift-report',
        merchantId,
        storeId,
        shiftId,
      ]);
      await qc.invalidateQueries(['shift-reports', merchantId, storeId]);
      await refetch();
      if (record?.download_url) {
        window.open(record.download_url, '_blank', 'noopener,noreferrer');
      }
      toast({
        title: t('Shift report ready'),
        status: 'success',
        duration: 3000,
      });
    } catch (e: any) {
      toast({
        title: t('Failed to generate shift report'),
        description: e?.response?.data?.message ?? e?.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Tooltip label={t('Generate PDF Shift Report')} hasArrow>
      <IconButton
        icon={<FiFileText />}
        size="xs"
        variant="ghost"
        colorScheme="blue"
        onClick={handleGenerate}
        isLoading={isGenerating || isLoading}
        aria-label={t('Generate PDF Shift Report')}></IconButton>
    </Tooltip>
  );
};

export default ShiftReportButton;
